import { z } from "zod";
import { UserRoleSchema } from "./enums.js";
import { EmailSchema, PasswordSchema, PinSchema } from "./auth.js";
import { BooleanQuerySchema, IndexedStringSchema, PhoneSchema, optionalQuery, } from "./primitives.js";
/**
 * User administration (spec §2, §13) — the contracts behind `user.manage`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS AT ALL. `user.manage` has been in the §2.1 capability
 * union since it was written, granted to the ADMIN and to nobody else, and until
 * 2026-08-17 there was NO ROUTE BEHIND IT. The CA's login shipped, her
 * capabilities were granted, and the only way to bring her account into
 * existence was a hand-written `INSERT` against the production database — which
 * means hashing a bcrypt password by hand on a live tax system and getting a
 * role string right with nothing to validate it against.
 *
 * `apps/api/scripts/create-user.ts` closed half of that (it can create all three
 * kinds, it prompts rather than taking the secret on the command line, and it
 * refuses to turn a credential reset into a role change), but it is a shell
 * program on the VPS. Nobody deactivates an account from it, nobody changes a
 * role with it — it refuses, deliberately — and it cannot be reached from the
 * app at all.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * NOTHING HERE EVER DESCRIBES A HASH, in either direction. `pin_hash` and
 * `password_hash` are server-side columns (§3, §14.2); a secret travels INTO
 * this API on a create or a credential reset and never comes back out of it —
 * see {@link UserResponse}, which is the only user shape these routes serialise.
 *
 * THE SECRET FIELD NAMES ARE `pin` AND `password`, AND THAT IS A TRAP WORTH
 * NAMING: `redactForAudit` (apps/api/src/services/audit.ts) blanks any key
 * matching `/(hash|secret|token|password|credential)/i`, plus a bare `pin`. That
 * is exactly right for these two — a request body must never be spread into an
 * audit entry, and if one ever is, the redactor catches it. What it means for
 * the code writing those entries is that audit payloads are built by hand from
 * named, non-secret fields (`signInWith`, `identifier`) rather than from the
 * body. The accountant work hit the other half of this trap and renamed a field
 * to `signInWith` because the obvious name silently logged `"[REDACTED]"`.
 */
/* -------------------------------------------------------------------------- */
/* 1. Reading                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * `GET /users?role=&active=&limit=&offset=`.
 *
 * `.strict()` like every other list contract here — an unknown key is a typo in
 * the client, and silently ignoring it means a filter that appears to work.
 *
 * THE DEFAULTS GO *INSIDE* `optionalQuery`, never after it. `optionalQuery` is a
 * `z.preprocess` that rewrites `""` to `undefined`; a `.default()` applied
 * OUTSIDE it only fires when the raw input was already undefined, so `?limit=`
 * — which the SPA sends whenever the field is blank — would reach
 * `z.coerce.number()` as `undefined`, coerce to `NaN` and answer 400.
 * `notifications.ts` shipped that bug on all four of its fields.
 *
 * There is no free-text search. This table holds three rows.
 */
export const UserListQuerySchema = z
    .object({
    role: optionalQuery(UserRoleSchema.optional()),
    active: optionalQuery(BooleanQuerySchema.optional()),
    limit: optionalQuery(z.coerce.number().int().min(1).max(100).default(50)),
    offset: optionalQuery(z.coerce.number().int().min(0).default(0)),
})
    .strict();
/**
 * The two doors a human can knock on, as ONE ACCOUNT'S ANSWER.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ANSWER SHIPS TO THE CLIENT; THE MAP DOES NOT. `ROLE_CREDENTIAL_PATH`
 * (apps/api/src/auth/credential-paths.ts) deliberately lives in the API rather
 * than here, and its header says why: it is AUTHENTICATION, and an
 * authentication rule compiled into the SPA is a rule somebody will eventually
 * be tempted to enforce there, where it is decoration. So the server computes
 * "this account signs in with email and a password" and puts the result on the
 * row; the client renders it and decides nothing.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const USER_SIGN_IN_METHODS = ["pin", "password"];
/* -------------------------------------------------------------------------- */
/* 2. Creating                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * `POST /users`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SHAPE IS VALIDATED HERE; WHICH CREDENTIAL THE ROLE TAKES IS DECIDED ON THE
 * SERVER, AND THE SPLIT IS DELIBERATE.
 *
 * All four credential fields are optional in this schema, and a request naming
 * the wrong pair is refused by the SERVICE with a 400 that names the field it
 * wanted. It would be tidier to express the binding as a discriminated union
 * right here — and it would mean compiling `ROLE_CREDENTIAL_PATH` into the SPA,
 * which is precisely what `auth/credential-paths.ts` refuses to do. One map, on
 * the server, read by the login service, by `create-user.ts` and by this route,
 * is what stops the account a screen can CREATE and the account the API will
 * ACCEPT from drifting apart. That drift is how the CA nearly ended up on a
 * 6-digit PIN.
 *
 * THE PASSWORD FLOOR IS THE SERVER'S TOO. `PasswordSchema` enforces what bcrypt
 * and the type demand — at least 8 characters, at most 72 BYTES, because bcrypt
 * silently ignores everything past 72 and would make two long passwords
 * interchangeable. The POLICY floor is `MIN_PASSWORD_LENGTH` in
 * `apps/api/src/auth/provisioning.ts` (10, with the argument for it written out
 * beside it), and this route holds to the same number as `create-user.ts` rather
 * than restating it.
 *
 * CREATING AN ACCOUNTANT WORKS WHILE `accountant_role` IS OFF. The account and
 * the session are different questions (§18.5): the row may exist for days before
 * the owner switches the flag on, and coupling the two would mean the one screen
 * that can make her account refuses to until a flag nobody has thought about yet
 * is set. The response carries a warning instead.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const UserCreateSchema = z
    .object({
    role: UserRoleSchema,
    displayName: IndexedStringSchema,
    /** Required for a role whose door is the PIN; refused for any other. */
    phone: PhoneSchema.optional(),
    /** Required for a role whose door is the password; refused for any other. */
    email: EmailSchema.optional(),
    pin: PinSchema.optional(),
    password: PasswordSchema.optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* 3. Updating                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * `PUT /users/:id` — the display name, the role, and whether the account is on.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NO CREDENTIAL FIELD, AND THAT IS THE SAME DECISION `create-user.ts` MADE FROM
 * THE OTHER DIRECTION.
 *
 * That script's re-run used to set `role` and `isActive` alongside the hash,
 * which turned the documented password-reset path into a silent
 * privilege-escalation tool (see `auth/provisioning.ts` for the full account of
 * it). The mirror-image mistake is available here: an edit form that also
 * carries a password means a rename can quietly reset a credential, and a
 * mistyped field in a client can lock somebody out of their own account.
 *
 * So the two are separate routes with separate audit actions. Setting a
 * credential is `POST /users/:id/credential`; this endpoint cannot reach a hash,
 * and `.strict()` means a client that sends one gets a 400 rather than having it
 * dropped on the floor.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * NO `DELETE /users/:id` EXISTS, ANYWHERE, and its absence is a design decision
 * rather than an omission — the same construction §2.1 uses for `invoice.edit`.
 * A user who has issued documents or authored audit rows must not vanish: the
 * schema already says so in four places (`Quotation.voidedBy`,
 * `DeliveryChallan.cancelledBy`, `CreditNote.cancelledBy` and `Payment.recordedBy`
 * are `SetNull`, so a departed user never takes the record of what they did;
 * `Message.author` is RESTRICT, so deleting somebody who has spoken is refused
 * outright). Deactivation is the operation, it is reversible, and it ends the
 * session on the very next request because `authenticate` re-reads the row.
 */
export const UserUpdateSchema = z
    .object({
    displayName: IndexedStringSchema.optional(),
    role: UserRoleSchema.optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((field) => field !== undefined), "Send at least one of displayName, role or isActive.");
/**
 * `POST /users/:id/credential` — set or reset the secret for this account's door.
 *
 * ONE ROUTE FOR BOTH DOORS, because the operation is the same operation and
 * splitting it would be a second place for the audit row, the lockout reset or
 * the hashing call to be forgotten — the argument `routes/auth.ts` already makes
 * for not giving the CA a login route of her own.
 *
 * EXACTLY ONE FIELD, MATCHING THE ROLE'S DOOR. Sending both, sending neither, or
 * sending the wrong one for the role is a 400 from the service, which is where
 * `ROLE_CREDENTIAL_PATH` lives.
 *
 * RESETTING A CREDENTIAL ALSO CLEARS THE LOCKOUT COUNTERS (§11A), for
 * `create-user.ts`'s reason: somebody locked out and asking for a new password
 * should not then have to wait out a cooling-off period for a credential that no
 * longer exists.
 */
export const UserCredentialSchema = z
    .object({
    pin: PinSchema.optional(),
    password: PasswordSchema.optional(),
})
    .strict();
/**
 * `POST /users/:id/identifier` — REPAIR the phone number or email address this
 * account signs in with.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT EXISTS BECAUSE `POST /profile/identifier` MADE A TYPO UNRECOVERABLE, AND
 * THAT IS THE WHOLE OF THE ARGUMENT FOR IT.
 *
 * `users.phone` and `users.email` are LOGIN IDENTIFIERS (§2, §10): half of what
 * a person types to get in. `/profile/identifier` is the only writer of either
 * after an account is created, and it demands the current credential — which
 * guards a BORROWED SESSION and does nothing at all about a MISTYPED VALUE. The
 * owner drops a digit from his own phone number, the write commits, 200; the
 * next `ownerLogin` looks up `where: { phone }` on the exact stored string and
 * answers the generic 401. He cannot authenticate, so he cannot reach
 * `/profile/identifier` to put it back — and until this schema existed nothing
 * else could either: `UserUpdateSchema` has no such field, `/users/:id/credential`
 * writes a hash, `/users/:id/doors` writes booleans, and re-running
 * `create-user.ts` keys on the identifier and so makes a SECOND account. The
 * recovery was a hand-written `UPDATE` against the live billing database.
 *
 * `/profile/identifier` still exists and is still the normal path — a person
 * changing their own email should not need an administrator. This is the repair
 * door: `user.manage`, one audit action shared with the self-service route
 * (`user.identifier_changed`, distinguished by `actor_user_id ≠ entity_id`), and
 * the same 409 when the value belongs to somebody else.
 *
 * NO CREDENTIAL FIELD, `.strict()`, exactly as `UserUpdateSchema` carries none:
 * repairing an identifier must not be able to reset a secret in the same
 * request, or the log has one row for two facts of very different weight.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * BOTH FIELDS OPTIONAL, EXACTLY ONE ACCEPTED. Which of the two a given account
 * may hold depends on its OPEN DOORS, which is a property of the row rather than
 * of the request — so the service refuses a phone number for an account whose
 * PIN door is shut (409), and this schema does not try to express it.
 */
export const UserIdentifierSchema = z
    .object({
    /** New sign-in phone. Only for an account whose PIN door is open. */
    phone: PhoneSchema.optional(),
    /** New sign-in email. Only for an account whose password door is open. */
    email: EmailSchema.optional(),
})
    .strict()
    .refine((value) => value.phone !== undefined || value.email !== undefined, 'Send the new "phone" or the new "email".');
/* -------------------------------------------------------------------------- */
/* 5. Which doors an account may sign in through (2026-08-18)                  */
/* -------------------------------------------------------------------------- */
/**
 * `POST /users/:id/doors` — open or close this account's sign-in methods.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ITS OWN ENDPOINT, TAKING NOTHING BUT THESE TWO BOOLEANS, AND THAT IS THE
 * SAFETY PROPERTY RATHER THAN A LAYOUT PREFERENCE.
 *
 * A door used to be implied by a role, and before that by which hash column
 * happened to be populated — which is how an ACCOUNTANT row written by hand on
 * the live box nearly ended up authenticating on a 6-digit PIN (the full account
 * is in `apps/api/src/auth/credential-paths.ts`). The owner now wants a person to
 * be able to hold either credential or both, so the door has to become data; the
 * question this schema answers is how it becomes data WITHOUT becoming something
 * a script can produce by accident.
 *
 * The answer is that opening a door is its own request, with its own capability
 * (`user.manage`), its own audit action per door that moved, and no other field
 * to hide behind. It cannot ride along on a rename, a role change or a password
 * reset, because none of those endpoints has a field for it — `UserUpdateSchema`
 * and `UserCredentialSchema` are both `.strict()`, and `UserCreateSchema` has no
 * door field either, so a NEW account gets the door its role starts with and
 * nothing else. Somebody has to mean it, and their name is on the row.
 *
 * BOTH FIELDS ARE OPTIONAL AND MEAN "LEAVE THIS DOOR AS IT IS"; at least one
 * must be sent. `{ "pin": true }` opens the PIN door and says nothing about the
 * password one, so an account can hold both.
 *
 * CLOSING THE LAST USABLE DOOR IS REFUSED BY THE SERVICE (409), not by this
 * schema — it depends on the row, not on the request.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const UserDoorsSchema = z
    .object({
    /** Phone + 6-digit PIN. `true` on a non-OWNER account is the audited decision. */
    pin: z.boolean().optional(),
    /** Email + password. */
    password: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.pin !== undefined || value.password !== undefined, 'Send at least one of "pin" or "password".');
/* -------------------------------------------------------------------------- */
/* 6. `/api/v1/profile` — MY OWN account (2026-08-18)                          */
/* -------------------------------------------------------------------------- */
/**
 * ═════════════════════════════════════════════════════════════════════════════
 * THE CONTRACTS FOR THE ONE ACCOUNT THE CALLER IS ALLOWED TO CHANGE: THEIR OWN.
 *
 * WHY THEY LIVE IN THIS FILE. They describe the SAME ROW as everything above —
 * `GET /profile` answers with a {@link UserResponse}, unchanged, because "my
 * account" and "an account" are the same shape seen from two sides. Splitting
 * them into a `profile.ts` would put two halves of one row's contract in two
 * files and invite them to drift.
 *
 * WHY THEY ARE NOT THE SAME ENDPOINTS. Everything above is behind `user.manage`,
 * which is ADMIN-only, which meant THE OWNER COULD NOT CHANGE HIS OWN PIN — his
 * son had to. Administering other people and maintaining your own account are
 * different grants; these are behind `profile.manage`, held by every role. See
 * that capability's note in permissions.ts.
 *
 * THREE PROPERTIES THE SCHEMAS THEMSELVES CARRY, none of which is left to a
 * handler to remember:
 *
 *  1. NO `role` AND NO `isActive` FIELD, ANYWHERE BELOW, and every schema is
 *     `.strict()`. Privilege escalation wearing a profile form is not refused
 *     here — it is UNTYPEABLE. A body carrying `"role": "ADMIN"` is a 400 from
 *     `validate()` before any handler runs, and the services name the columns
 *     they write rather than spreading a body, so there is no second path
 *     either. (`assertAdministrationSurvives` guards a different door — the
 *     ADMIN screen — and nothing here may lean on it.)
 *  2. NO `id` FIELD AND NO `:id` IN ANY PATH. The target is `req.user.id`, so
 *     there is no request that can be aimed at somebody else's account.
 *  3. A CREDENTIAL CHANGE CARRIES THE CURRENT CREDENTIAL. Without it a borrowed
 *     session — an unlocked phone on a workbench for ninety seconds — is a
 *     permanent account takeover: change the password, change the email, and the
 *     proprietor is locked out of his own billing system by somebody who never
 *     knew a secret. The server verifies it on the same terms as a sign-in,
 *     including the per-account lockout, so this endpoint cannot be walked
 *     through a 10^6 PIN keyspace either.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/**
 * The re-authentication half of a profile write, mixed into the two schemas
 * that need it.
 *
 * BOTH ARE OPTIONAL IN THE SHAPE and exactly one is required by the SERVER,
 * which decides against the caller's own open doors — an account holding both
 * credentials may prove either. Expressing that as a discriminated union here
 * would mean the client deciding which door it is allowed to use, and
 * `apps/api/src/auth/credential-paths.ts` is explicit that authentication rules
 * do not ship to the SPA.
 *
 * THE FIELD NAMES CARRY A TRAP THAT IS WORTH KNOWING RATHER THAN REDISCOVERING:
 * `redactForAudit` blanks any key matching `/(hash|secret|token|password|credential)/i`
 * plus a bare `pin`, so BOTH of these are redacted if a body is ever spread into
 * an audit entry. That is exactly right and is a net, not the floor — the
 * services build their audit payloads by hand from named, non-secret fields.
 */
const CurrentCredentialShape = {
    /** For an account whose PIN door is open. */
    currentPin: PinSchema.optional(),
    /** For an account whose password door is open. */
    currentPassword: z.string().min(1, "Enter your current password").optional(),
};
/**
 * `PUT /profile` — my display name, and nothing else.
 *
 * NO CURRENT CREDENTIAL IS REQUIRED, and the asymmetry with the two routes below
 * is deliberate. A display name is not a login identifier and not a secret: it
 * is what the audit trail calls you. Getting it wrong is embarrassing and
 * instantly reversible by the person who did it, so demanding a password to fix
 * a typo would be friction with nothing behind it. An email address or a PIN
 * typed wrong locks you out of the account, which is why those two ask.
 */
export const ProfileUpdateSchema = z
    .object({
    displayName: IndexedStringSchema,
})
    .strict();
/**
 * `POST /profile/credential` — change my own PIN or my own password.
 *
 * EXACTLY ONE NEW SECRET, FOR A DOOR THAT IS ALREADY OPEN ON MY ACCOUNT. A
 * person cannot open a door for themselves: that is `user.manage`'s
 * `POST /users/:id/doors`, and keeping the two apart is what keeps "the owner
 * decided the CA may use a PIN" and "she gave herself one" different events.
 *
 * The new secret may be for a DIFFERENT door than the one used to authenticate —
 * an account holding both proves itself with either and rotates the other, which
 * is how a compromised password gets replaced from a phone.
 */
export const ProfileCredentialSchema = z
    .object({
    ...CurrentCredentialShape,
    /** The new 6-digit PIN. Only for an account whose PIN door is open. */
    pin: PinSchema.optional(),
    /** The new password. The 10-character POLICY floor is the server's. */
    password: PasswordSchema.optional(),
})
    .strict()
    .refine((value) => value.pin !== undefined || value.password !== undefined, 'Send the new "pin" or the new "password".');
/**
 * `POST /profile/identifier` — change the email address or phone number I SIGN
 * IN WITH.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THESE ARE NOT CONTACT DETAILS, AND THE ROUTE IS SHAPED BY THAT.
 *
 * `users.email` and `users.phone` are the two login identifiers (§2, §10) —
 * email for the password door, phone for the PIN door — and both are UNIQUE
 * columns. A mistyped new value does not produce a bounced email; it locks that
 * person out of their own account, because the identifier is half the credential
 * and nobody but an ADMIN can put it back. So this endpoint asks for the current
 * credential exactly as the one above does, and the server refuses a value that
 * belongs to somebody else with a 409 rather than silently taking it.
 *
 * The business's own contact details — the address, the phone printed on an
 * invoice — are `/company` (§7.3) and have nothing to do with this row.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const ProfileIdentifierSchema = z
    .object({
    ...CurrentCredentialShape,
    /** New sign-in phone. Only for an account whose PIN door is open. */
    phone: PhoneSchema.optional(),
    /** New sign-in email. Only for an account whose password door is open. */
    email: EmailSchema.optional(),
})
    .strict()
    .refine((value) => value.phone !== undefined || value.email !== undefined, 'Send the new "phone" or the new "email".');
//# sourceMappingURL=users.js.map